import { validateEmail } from '@calefy-inc/authentication';
import { Quote } from '../../../../../../../Typescript/backend/classes';
import { InsurancePolicy } from '../../../../../../../Typescript/classes';
import { safelyGetAttributes } from '../../../../../../../util';
import { formatAsReadableList } from '../../../../../../common/QuoteComponents/utility';

export const validateVagoEmailAddress = (email: string) => {
  const emailResult = validateEmail(email);
  if (emailResult) {
    return emailResult;
  }
  const split = email.split('@');
  if (split.length !== 2) {
    return `Email ${email} does not have the correct format`;
  }
  const domain = split[1];
  const vago_domain = 'vagoassure.com';
  if (domain !== vago_domain) {
    return `Email must be from a ${vago_domain} email`;
  }
  return;
};

export const validateRequiredEmailList = (emails?: string) => {
  if (!emails) {
    return 'This field is required';
  }
  const splitEmails = emails.split(',').map((email) => email.trim());
  const badEmails = splitEmails.reduce((bad: Array<string>, email) => {
    const result = validateEmail(email);
    if (result) {
      bad.push(email);
    }
    return bad;
  }, []);
  if (badEmails.length > 0) {
    return `Found invalid email${
      badEmails.length > 1 ? 's' : ''
    } ${formatAsReadableList(badEmails)}`;
  }
};

export const validateRequired = (value?: string | null) => {
  if (!value) {
    return 'This field is required';
  }
  return;
};

export const generateSendToBrokerEmailBody = (
  // @ts-expect-error
  q: Quote,
  insurancePolicies: Array<InsurancePolicy>,
): string => {
  let totalPropertyPremium = 0;
  let totalLiabilityPremium = 0;
  let totalBoilerPremium = 0;
  let fees = 0;
  let inspectionFees = 0;
  let totalPremiumIncludingFees = 0;
  let vagoCommissionPercent = 0;
  let legalSolutionsPremium = 0;
  insurancePolicies.forEach((policy) => {
    // get the combined property premium
    policy.coverages
      .filter((coverage) => coverage.name === 'Property')
      .forEach((coverage) => {
        totalPropertyPremium += coverage.perils.reduce((sum, peril) => {
          if (
            ['Crime', 'Legal Solutions', 'Equipment Breakdown'].includes(
              peril.name,
            )
          ) {
            return sum;
          }
          return sum + peril.premium;
        }, 0);
      });

    // get the combined liability premium
    policy.coverages
      .filter((coverage) => coverage.name === 'Liability')
      .forEach((coverage) => {
        totalLiabilityPremium += coverage.perils.reduce((sum, peril) => {
          return sum + peril.premium;
        }, 0);
      });

    // get the total legal solutions premium
    policy.coverages.forEach((coverage) => {
      coverage.perils
        .filter((peril) => peril.name === 'Legal Solution')
        .forEach((peril) => {
          legalSolutionsPremium += peril.premium;
        });
    });

    // get the total equipment breakdown / boiler premium
    policy.coverages.forEach((coverage) => {
      coverage.perils
        .filter((peril) => peril.name === 'Equipment Breakdown')
        .forEach((peril) => {
          totalBoilerPremium += peril.premium;
        });
    });

    // get the inspection fee
    const { additionalInformation } = policy;
    const rawFees = safelyGetAttributes(additionalInformation, ['vago_fees']);
    // @ts-expect-error
    fees += rawFees || 0;

    const rawInspectionFees = safelyGetAttributes(additionalInformation, [
      'inspection_fees',
    ]);
    // @ts-expect-error
    inspectionFees += rawInspectionFees || 0;

    if (policy.commissionRate) {
      vagoCommissionPercent = policy.commissionRate;
    }
  });

  totalPremiumIncludingFees =
    fees +
    inspectionFees +
    totalPropertyPremium +
    totalLiabilityPremium +
    legalSolutionsPremium +
    totalBoilerPremium;

  let totalPremium = totalPropertyPremium + totalLiabilityPremium;

  return `Hello,

Please find attached the quote for your review. I invite you to carefully review the details of the coverages and subjectivities before providing your instructions.

· Premium: ${vagoFormatDollar(totalPremium)}
  o Property Premium: ${vagoFormatDollar(totalPropertyPremium)}
  o Liability Premium: ${vagoFormatDollar(totalLiabilityPremium)}
    · Boiler Premium: ${vagoFormatDollar(totalBoilerPremium)}
    · Fees: ${vagoFormatDollar(fees)}
    · Inspection Fees: ${vagoFormatDollar(inspectionFees)}
    · Total Premium including all fees: ${vagoFormatDollar(
      totalPremiumIncludingFees,
    )}
    · Commission (excluding fees): ${vagoFormatPercent(vagoCommissionPercent)}

 Please note that ARAG Legal Solutions is available for ${vagoFormatDollar(
   legalSolutionsPremium,
 )}. This coverage will only be included if explicitly requested.`;
};

export const vagoFormatDollar = (value: number): string => {
  if (value === 0) {
    return '-';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const vagoFormatPercent = (percent: number): string => {
  if (percent === 0) return '-';
  return `${(percent * 100).toFixed(2)}%`;
};

export const generateEmailSubject = (q: Quote) => {
  const accountName = q.structuredData?.businessName
    ? q.structuredData.businessName
    : '';

  const policyTypeAnswer = q.getAnswerValueByApiName('policy_type');
  const businessType = policyTypeAnswer ? `${policyTypeAnswer} Quote` : ''; // renewal, new business, &c.
  const subject = [businessType, accountName]
    .filter((elem) => !!elem)
    .join(' / ')
    .trim();
  return subject;
};
